/**
 * Right column: what the run produced, laid out exactly like native OpenADS's
 * output folder (`<subject>/<DWI|PWI|qc>/<stage>/<file>`). A summary read from
 * the run's own report files sits on top; below it the folder tree and, once
 * a file is picked, its preview (images go to the centre viewer instead).
 * The tree and the preview share the column through a draggable divider.
 */

import * as React from "react";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileCode2,
  FileImage,
  FileJson,
  FileText,
  Folder,
  FolderOpen,
  Layers,
  Loader2,
  Scan,
  Table2,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Splitter } from "@/shell/Splitter";
import { usePipelineSession } from "@/pipeline/session";
import { extOf, resolveUrl, useSimple, type SimpleFile } from "./store";
import { buildZip } from "./zip";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

async function readText(id: string): Promise<string> {
  const url = await resolveUrl(id);
  if (!url) throw new Error("This file is no longer in memory.");
  return (await fetch(url)).text();
}

async function download(file: SimpleFile) {
  const url = await resolveUrl(file.id);
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
}

// ---------------------------------------------------------------- summary ---

interface Finding {
  modality: "DWI" | "PWI";
  volumeMl: number | null;
  side: string | null;
  aspects: string | null;
  file: SimpleFile;
}

function parseReport(text: string): Pick<Finding, "volumeMl" | "side" | "aspects"> {
  const vol = text.match(/with\s+([\d.]+)\s*ml/i);
  const aspects = text.match(/ASPECTS is\s+(\d+)/i);
  const side = text.match(/is (unilateral|bilateral)(?:, within the (left|right))?/i);
  return {
    volumeMl: vol ? Number(vol[1]) : null,
    aspects: aspects ? aspects[1] : null,
    side: side ? (side[2] ? `${side[2]} hemisphere` : side[1]) : null,
  };
}

function Summary({ reports }: { reports: SimpleFile[] }) {
  const [findings, setFindings] = React.useState<Finding[]>([]);

  React.useEffect(() => {
    let live = true;
    void Promise.all(
      reports.map(async (file) => ({
        ...parseReport(await readText(file.id).catch(() => "")),
        modality: (file.id.includes("/PWI/") ? "PWI" : "DWI") as Finding["modality"],
        file,
      })),
    ).then((f) => live && setFindings(f));
    return () => {
      live = false;
    };
  }, [reports]);

  if (findings.length === 0) return null;
  const core = findings.find((f) => f.modality === "DWI")?.volumeMl ?? null;
  const hp = findings.find((f) => f.modality === "PWI")?.volumeMl ?? null;
  return (
    <div className="shrink-0 border-b border-ads-border bg-ads-subtle px-3 py-3">
      <div className={cn("grid gap-2", findings.length > 1 ? "grid-cols-2" : "grid-cols-1")}>
        {findings.map((f) => (
          <button
            key={f.file.id}
            type="button"
            onClick={() => useSimple.getState().setPreview(f.file.id)}
            className="group rounded-lg border border-ads-border bg-ads-surface px-3 py-2.5 text-left shadow-sm transition hover:border-ads-accent-border hover:shadow"
          >
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ads-faint">
              <span className={cn("h-1.5 w-1.5 rounded-full", f.modality === "PWI" ? "bg-orange-500" : "bg-ads-accent")} />
              {f.modality === "PWI" ? "Hypoperfusion" : "Stroke core"}
            </div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="text-[24px] font-semibold leading-none tabular-nums text-ads-text">{f.volumeMl ?? "—"}</span>
              <span className="text-[11px] text-ads-muted">mL</span>
            </div>
            <div className="mt-1 truncate text-[11px] text-ads-muted">
              {[f.side, f.modality === "DWI" && f.aspects != null ? `ASPECTS ${f.aspects}` : null].filter(Boolean).join(" · ")}
            </div>
          </button>
        ))}
      </div>
      {core != null && hp != null && core > 0 ? (
        <div className="mt-2 flex items-center justify-between rounded-md border border-dashed border-ads-border px-3 py-1.5 text-[11px] text-ads-muted">
          <span>Mismatch (HP − core)</span>
          <span className="font-medium tabular-nums text-ads-text">
            {(hp - core).toFixed(2)} mL · ratio {(hp / core).toFixed(2)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- preview ---

function CsvTable({ text }: { text: string }) {
  const rows = text.trim().split(/\r?\n/).map((r) => r.split(","));
  const [head, ...body] = rows;
  // One-row tables (QFV, lesion load) read better turned on their side.
  if (body.length === 1 && head.length > 4) {
    return (
      <table className="w-full text-[11.5px]">
        <tbody>
          {head.map((h, i) => (
            <tr key={h + i} className="border-b border-ads-border last:border-0">
              <td className="py-1 pr-3 text-ads-muted">{h}</td>
              <td className="py-1 text-right font-mono tabular-nums">{body[0][i]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <table className="w-full text-[11.5px]">
      <thead className="sticky top-0 bg-ads-surface">
        <tr>
          {head.map((h, i) => (
            <th key={h + i} className="border-b border-ads-border-strong py-1 pr-2 text-left font-medium">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {body.map((r, i) => (
          <tr key={i} className="border-b border-ads-border">
            {r.map((c, j) => (
              <td key={j} className="py-1 pr-2 font-mono tabular-nums">
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** HTML that references sibling files (qc_report.html → qc/DWI/*.png) gets those links rewritten to the in-memory files. */
async function resolveHtml(file: SimpleFile, html: string, all: SimpleFile[]): Promise<string> {
  const dir = file.id.split("/").slice(0, -1).join("/");
  const refs = [...html.matchAll(/(?:src|href)="([^"#:]+)"/g)].map((m) => m[1]);
  let out = html;
  for (const ref of new Set(refs)) {
    // Native qc_report.html links relative to the subject folder, not to qc/.
    const runRoot = file.id.split("/")[0];
    const target = all.find((f) => f.id === `${dir}/${ref}`.replace(/\/\.\//g, "/")) ?? all.find((f) => f.id === `${runRoot}/${ref}`);
    const url = target ? await resolveUrl(target.id) : null;
    if (url) out = out.split(`"${ref}"`).join(`"${url}"`);
  }
  return out;
}

function Preview({ file, all }: { file: SimpleFile; all: SimpleFile[] }) {
  const [content, setContent] = React.useState<{ text?: string; url?: string } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const ext = extOf(file.name);

  React.useEffect(() => {
    let live = true;
    setContent(null);
    setError(null);
    void (async () => {
      try {
        if (ext === "png" || ext === "pdf" || ext === "jpg") {
          const url = await resolveUrl(file.id);
          if (!url) throw new Error("This file is no longer in memory.");
          if (live) setContent({ url });
        } else if (ext === "mat") {
          if (live) setContent({});
        } else {
          let text = await readText(file.id);
          if (ext === "html") text = await resolveHtml(file, text, all);
          if (live) setContent({ text });
        }
      } catch (e) {
        if (live) setError((e as Error).message);
      }
    })();
    return () => {
      live = false;
    };
  }, [file.id, ext, all]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-ads-border bg-ads-subtle px-3 py-1.5">
        <FileGlyph file={file} />
        <span className="min-w-0 flex-1 truncate text-[12px] font-medium" title={file.name}>
          {file.name}
        </span>
        <span className="text-[10.5px] text-ads-faint">{formatSize(file.sizeBytes)}</span>
        <button type="button" title="Download" onClick={() => void download(file)} className="rounded p-1 text-ads-muted hover:bg-ads-hover hover:text-ads-text">
          <Download className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Close preview"
          onClick={() => useSimple.getState().setPreview(null)}
          className="rounded p-1 text-ads-muted hover:bg-ads-hover hover:text-ads-text"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto bg-ads-surface">
        {error ? (
          <p className="p-3 text-[12px] text-red-600">{error}</p>
        ) : content == null ? (
          <p className="p-3 text-[12px] text-ads-muted">Loading…</p>
        ) : ext === "png" || ext === "jpg" ? (
          <img src={content.url} alt={file.name} className="w-full" />
        ) : ext === "pdf" ? (
          <iframe title={file.name} src={content.url} className="h-full w-full border-0" />
        ) : ext === "mat" ? (
          <p className="p-3 text-[12px] text-ads-muted">A binary ANTs transform. Download it to use it in ANTs.</p>
        ) : ext === "csv" ? (
          <div className="p-3">
            <CsvTable text={content.text ?? ""} />
          </div>
        ) : ext === "html" ? (
          <iframe title={file.name} srcDoc={content.text} sandbox="allow-same-origin" className="h-full w-full border-0" />
        ) : (
          <pre className="whitespace-pre-wrap break-words p-3 font-mono text-[11px] leading-relaxed text-neutral-700">{content.text}</pre>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- tree ---

function FileGlyph({ file }: { file: SimpleFile }) {
  const ext = extOf(file.name);
  if (file.isMask) return <Layers className="h-3.5 w-3.5 shrink-0 text-amber-500" />;
  if (file.isVolume) return <Scan className="h-3.5 w-3.5 shrink-0 text-ads-accent" />;
  if (ext === "csv") return <Table2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />;
  if (ext === "png" || ext === "jpg") return <FileImage className="h-3.5 w-3.5 shrink-0 text-violet-500" />;
  if (ext === "pdf") return <FileText className="h-3.5 w-3.5 shrink-0 text-rose-500" />;
  if (ext === "json" || ext === "yaml") return <FileJson className="h-3.5 w-3.5 shrink-0 text-ads-muted" />;
  if (ext === "html") return <FileCode2 className="h-3.5 w-3.5 shrink-0 text-ads-muted" />;
  return <FileText className="h-3.5 w-3.5 shrink-0 text-ads-muted" />;
}

interface TreeNode {
  name: string;
  path: string;
  dirs: TreeNode[];
  files: SimpleFile[];
}

function buildTree(rootName: string, files: SimpleFile[], strip: (id: string) => string[]): TreeNode {
  const root: TreeNode = { name: rootName, path: rootName, dirs: [], files: [] };
  for (const f of files) {
    const parts = strip(f.id);
    let node = root;
    for (const dir of parts.slice(0, -1)) {
      let next = node.dirs.find((d) => d.name === dir);
      if (!next) {
        next = { name: dir, path: `${node.path}/${dir}`, dirs: [], files: [] };
        node.dirs.push(next);
      }
      node = next;
    }
    node.files.push(f);
  }
  // Native order: DWI, PWI, qc; stages in pipeline order; files by name.
  const ORDER = ["DWI", "PWI", "qc", "preprocess", "registration", "segment", "segmentation", "reporting"];
  const sort = (n: TreeNode) => {
    n.dirs.sort((a, b) => {
      const ia = ORDER.indexOf(a.name);
      const ib = ORDER.indexOf(b.name);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.name.localeCompare(b.name);
    });
    n.files.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    n.dirs.forEach(sort);
  };
  sort(root);
  return root;
}

function countFiles(n: TreeNode): number {
  return n.files.length + n.dirs.reduce((a, d) => a + countFiles(d), 0);
}

function filesUnder(node: TreeNode, prefix: string): { path: string; file: SimpleFile }[] {
  return [
    ...node.files.map((file) => ({ path: `${prefix}${file.name}`, file })),
    ...node.dirs.flatMap((d) => filesUnder(d, `${prefix}${d.name}/`)),
  ];
}

/** Zip a folder (with its sub-folders) under its own name, as it sits in the native output tree. */
async function downloadFolder(node: TreeNode, setBusy: (b: boolean) => void) {
  setBusy(true);
  try {
    const entries: { path: string; blob: Blob }[] = [];
    for (const { path, file } of filesUnder(node, `${node.name}/`)) {
      const url = await resolveUrl(file.id);
      if (url) entries.push({ path, blob: await (await fetch(url)).blob() });
    }
    const zip = await buildZip(entries);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(zip);
    a.download = `${node.path.replace(/\//g, "_")}.zip`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
  } finally {
    setBusy(false);
  }
}

function DirRow({
  node,
  depth,
  open,
  onToggle,
}: {
  node: TreeNode;
  depth: number;
  open: boolean;
  onToggle: () => void;
}) {
  const Icon = open ? FolderOpen : Folder;
  const [busy, setBusy] = React.useState(false);
  return (
    <div
      className="group flex w-full items-center gap-1.5 rounded py-[3px] pr-1 text-[12px] text-ads-text hover:bg-ads-hover"
      style={{ paddingLeft: 6 + depth * 14 }}
    >
      <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
        {open ? <ChevronDown className="h-3 w-3 shrink-0 text-ads-faint" /> : <ChevronRight className="h-3 w-3 shrink-0 text-ads-faint" />}
        <Icon className="h-3.5 w-3.5 shrink-0 text-sky-600" />
        <span className="min-w-0 flex-1 truncate font-medium">{node.name}</span>
      </button>
      <span className={cn("shrink-0 rounded-full bg-ads-bg px-1.5 text-[10px] tabular-nums text-ads-faint", !busy && "group-hover:hidden", busy && "hidden")}>
        {countFiles(node)}
      </span>
      <button
        type="button"
        title={`Download ${node.name} as .zip`}
        disabled={busy}
        onClick={() => void downloadFolder(node, setBusy)}
        className={cn("shrink-0 rounded p-0.5 text-ads-muted hover:bg-ads-active hover:text-ads-text", busy ? "block" : "hidden group-hover:block")}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
      </button>
    </div>
  );
}

function FileRow({ file, depth, state }: { file: SimpleFile; depth: number; state: "base" | "overlay" | "preview" | null }) {
  return (
    <div
      className={cn(
        "group flex items-center gap-1.5 rounded py-[3px] pr-1 text-[12px]",
        state ? "bg-ads-accent-soft text-ads-accent" : "text-ads-text hover:bg-ads-hover",
      )}
      style={{ paddingLeft: 6 + depth * 14 + 15 }}
    >
      <button
        type="button"
        onClick={() => useSimple.getState().show(file)}
        title={file.isMask ? "Lay over the current image" : file.isVolume ? "Show in the viewer" : "Preview"}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
      >
        <FileGlyph file={file} />
        <span className="min-w-0 flex-1 truncate">{file.name}</span>
      </button>
      {state === "base" ? <span className="shrink-0 rounded bg-ads-accent px-1 text-[9.5px] font-semibold text-white">BASE</span> : null}
      {state === "overlay" ? <span className="shrink-0 rounded bg-amber-500 px-1 text-[9.5px] font-semibold text-white">OVERLAY</span> : null}
      <span className="shrink-0 text-[10.5px] tabular-nums text-ads-faint group-hover:hidden">{formatSize(file.sizeBytes)}</span>
      <button
        type="button"
        title="Download"
        onClick={() => void download(file)}
        className="hidden shrink-0 rounded p-0.5 text-ads-muted hover:bg-ads-active hover:text-ads-text group-hover:block"
      >
        <Download className="h-3 w-3" />
      </button>
    </div>
  );
}

function Tree({ node, depth, openDirs, setOpen }: { node: TreeNode; depth: number; openDirs: Record<string, boolean>; setOpen: (p: string, o: boolean) => void }) {
  const baseId = useSimple((s) => s.baseId);
  const overlayIds = useSimple((s) => s.overlayIds);
  const previewId = useSimple((s) => s.previewId);
  // Folded by default: registration (dozens of intermediates) and the moco transforms.
  const isOpen = (n: TreeNode) => openDirs[n.path] ?? !/registration$|moco/.test(n.path);
  return (
    <>
      {node.dirs.map((d) => (
        <React.Fragment key={d.path}>
          <DirRow node={d} depth={depth} open={isOpen(d)} onToggle={() => setOpen(d.path, !isOpen(d))} />
          {isOpen(d) ? <Tree node={d} depth={depth + 1} openDirs={openDirs} setOpen={setOpen} /> : null}
        </React.Fragment>
      ))}
      {node.files.map((f) => (
        <FileRow
          key={f.id}
          file={f}
          depth={depth}
          state={f.id === baseId ? "base" : overlayIds.includes(f.id) ? "overlay" : f.id === previewId ? "preview" : null}
        />
      ))}
    </>
  );
}

// ------------------------------------------------------------------ panel ---

export function OutputsPanel() {
  const inputs = useSimple((s) => s.inputs);
  const outputs = useSimple((s) => s.outputs);
  const previewId = useSimple((s) => s.previewId);
  const subjectId = usePipelineSession((s) => s.draft.subjectId) || "subject";

  const runs = React.useMemo(() => [...new Set(outputs.map((f) => f.run))].sort((a, b) => b - a), [outputs]);
  const [run, setRun] = React.useState<number | null>(null);
  const shownRun = run != null && runs.includes(run) ? run : runs[0] ?? null;
  const [openDirs, setOpenDirs] = React.useState<Record<string, boolean>>({});
  const setOpen = React.useCallback((p: string, o: boolean) => setOpenDirs((d) => ({ ...d, [p]: o })), []);
  const [previewHeight, setPreviewHeight] = React.useState(320);

  const inRun = React.useMemo(() => outputs.filter((f) => f.run === shownRun), [outputs, shownRun]);
  const outTree = React.useMemo(() => buildTree(subjectId, inRun, (id) => id.split("/").slice(1)), [subjectId, inRun]);
  const reports = React.useMemo(() => inRun.filter((f) => /reporting\/.*_automatic_radiological_report\.txt$/.test(f.id)), [inRun]);
  const all = React.useMemo(() => [...inputs, ...outputs], [inputs, outputs]);
  const preview = all.find((f) => f.id === previewId) ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col border-l border-ads-border bg-ads-surface">
      <div className="flex shrink-0 items-center gap-2 border-b border-ads-border px-4 py-2.5">
        <h2 className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ads-faint">Outputs</h2>
        {inRun.length ? <span className="text-[10.5px] text-ads-faint">{inRun.length} files</span> : null}
        {runs.length > 1 ? (
          <select
            value={shownRun ?? ""}
            onChange={(e) => setRun(Number(e.target.value))}
            className="ml-auto h-6 rounded border border-ads-border-strong bg-ads-surface px-1 text-[11px]"
          >
            {runs.map((r) => (
              <option key={r} value={r}>
                Run {r}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <Summary reports={reports} />

      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5">
        {outputs.length === 0 ? (
          <p className="px-3 py-2 text-[11.5px] text-ads-muted">Nothing yet — files appear here as each step finishes, in the same folders native OpenADS writes.</p>
        ) : (
          <>
            <DirRow node={outTree} depth={0} open={openDirs[outTree.path] ?? true} onToggle={() => setOpen(outTree.path, !(openDirs[outTree.path] ?? true))} />
            {openDirs[outTree.path] ?? true ? <Tree node={outTree} depth={1} openDirs={openDirs} setOpen={setOpen} /> : null}
          </>
        )}
      </div>

      {preview ? (
        <>
          <Splitter orientation="horizontal" edge="end" size={previewHeight} min={140} max={900} onResize={setPreviewHeight} />
          <div className="shrink-0" style={{ height: previewHeight }}>
            <Preview file={preview} all={all} />
          </div>
        </>
      ) : null}
    </div>
  );
}
